import React, { use, useEffect } from "react";
import _, { set } from "lodash";
import cx from 'classnames';

import { translations } from "../../i18n";

import styles from './seek.module.scss';
import { Tooltip } from "react-tooltip";
import { TermsPane } from "./components/terms";
import { SearchInputBar } from "./components/searchInput";
import { SeekSessionContainer } from "./components/session";

import Modal from 'react-modal';
import { useSelector } from "react-redux";
import { invalidateSeekSession, seekAnswerForQuestion } from "../../redux/actions/seek";
import { useDispatch } from "react-redux";
import { Dataset } from "../../types/dataseek";

interface SeekPanelProps {
  className?: string;
  open: boolean;
  dataset: Dataset;
  session?: any;
  locale?: string;
  querying?: boolean;
  initialQuestion?: string;
  onAnswer?: (question: string) => void;
  onNewChat?: () => void;
  onClose?: () => void;
}

export const SeekPanel: React.FC<SeekPanelProps> = ({ className, open, dataset, session, locale, querying, initialQuestion, onAnswer, onNewChat, onClose }) => {
  const suggestions = (translations.translation['seek.suggestions'] as string[]) || [];
  const [showChats, setShowChats] = React.useState<boolean>(false);
  const [question, setQuestion] = React.useState<string>('');
  const [tutorial, setTutorial] = React.useState<boolean>(false);
  const [terms, setTerms] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const answerQuestion = React.useCallback((message: any) => {
    onAnswer?.(message);

    setShowChats(true);
    setQuestion('');
  }, [onAnswer]);

  const handleSubmit = () => {
    if (question.trim().length === 0) {
      return;
    }

    answerQuestion({
      type: 'text',
      payload: question
    });
  }

  const handleNewChatClicked = () => {

    setShowChats(false);
    setQuestion('');
    setLoading(false);

    onNewChat?.();
  }

  const handleSummarizeClicked = () => {
    const question = translations.translation["seek.summary_question"];
    answerQuestion({
      type: 'text',
      payload: question
    });
  }

  const handleSuggestionClicked = (text: string) => {
    answerQuestion({
      type: 'text',
      payload: text
    });
  }

  useEffect(() => {
    if(initialQuestion){
      answerQuestion({
        type: 'text',
        payload: initialQuestion
      });
    }
  }, [initialQuestion]);

  useEffect(() => {
    setLoading(querying || false);
  }, [querying]);

  return (
    <div className={cx(styles.panel, {
      [styles.showChats]: showChats
    }, className)}>
      <div className={cx(styles.panelBody, 'seek-panel__body')}>
        <div className={cx(styles.header, 'seek-header')}>
          <div className={cx(styles.name, 'seek-header__name')}>seek</div>
          <div className={cx(styles.branding, 'seek-header__branding')}>
            <div className={cx(styles.powered_by, 'seek-header__powered_by')}>Powered by AI</div>
            <div className={cx(styles.paperflite, 'seek-header__paperflite')}>by <img src={`${process.env.IMAGE_CDN_URL}/images/paperflite.svg`} /></div>
          </div>
        </div>

        <div className={cx(styles.questionBarContainer, 'seek-question-bar-container')}>
          <div className={styles.heading}>{translations.translation["seek.ask_question"]}</div>
          <SearchInputBar className={styles.questionBar}
            value={question}
            disabled={loading}
            placeholder={loading ? translations.translation["seek.awaiting_response"] : null}
            onChange={(value: string) => {
              setQuestion(value);
            }}
            onSubmit={handleSubmit}
          />

          {(suggestions || []).length > 0 &&
            <div className={cx(styles.suggestions, 'seek-suggestions')}>
              <div className={styles.heading}>
                <img src={`${process.env.IMAGE_CDN_URL}/images/cursor.svg`} /> {translations.translation["seek.try_suggestions_heading"]}
              </div>
              <ul>
                {(suggestions || []).map((text: string, index: number) => {
                  return (
                    <li key={index} className={styles.suggestion} onClick={() => handleSuggestionClicked(text)}>{text}</li>
                  )
                })}
              </ul>
            </div>
          }
        </div>


        {showChats &&
          <div id={'seek_session_container'} className={cx(styles.sessionContainer, 'seek-session-container')}>
            <div className={styles.scrollContainer}>
              <SeekSessionContainer id={session}
                dataset={dataset}
                answerQuestion={answerQuestion}
              />
            </div>
          </div>
        }

        <div className={cx(styles.footer, 'seek-footer')}>
          <div className={styles.actions}>
            <div className={styles.left}>
              <button id="terms-and-conditions-button" className={styles.action} onClick={() => setTutorial(true)}>
                <img src={`${process.env.IMAGE_CDN_URL}/images/seek/info.svg`} />
              </button>
              <button className={styles.action} onClick={() => setTerms(true)}>
                <img src={`${process.env.IMAGE_CDN_URL}/images/seek/terms.svg`} />
              </button>
              <Tooltip anchorSelect="#how-to-use-seek-button" place='top' content={"How to use seek"}>
                
              </Tooltip>
              <Tooltip id="terms-and-conditions-button" place='top' content={"Terms & \nConditions"}>
                
              </Tooltip>
            </div>
            <div className={styles.right}>
              <button className={styles.action} onClick={handleNewChatClicked} disabled={loading}>
                <img src={`${process.env.IMAGE_CDN_URL}/images/seek/plus.svg`} /> {translations.translation["seek.action.new_chat"]}
              </button>
              <button className={styles.action} onClick={handleSummarizeClicked} disabled={loading}>
                <img src={`${process.env.IMAGE_CDN_URL}/images/seek/summarize.svg`} /> {translations.translation["seek.action.summarize"]}
              </button>
            </div>
          </div>
          {locale === 'en' &&
            <div className={styles.disclaimer}>
              By using Seek, you accept and agree to be bound and abide by these <a className={styles.termsLink} href="#" onClick={() => setTerms(true)}>terms and conditions</a>.
            </div>
          }

          {locale === 'es' &&
            <div className={styles.disclaimer}>
              Al utilizar Seek, usted acepta y se compromete a cumplir estos <a className={styles.termsLink} href="#" onClick={() => setTerms(true)}>términos y condiciones</a>.
            </div>
          }

          {locale === 'pt-br' &&
            <div className={styles.disclaimer}>
              Ao usar o Seek, você aceita e concorda em obedecer e cumprir estes <a className={styles.termsLink} href="#" onClick={() => setTerms(true)}>termos e condições</a>.
            </div>
          }
        </div>
      </div>
      {tutorial &&
        <TutorialPane onClose={() => {
          setTutorial(false);
        }} />
      }
      {terms &&
        <TermsPane locale={locale || 'en'} onClose={() => {
          setTerms(false);
        }} />
      }
    </div>
  );
}

interface TutorialPaneProps {
  onClose: () => void;
}

export const TutorialPane: React.FC<TutorialPaneProps> = ({ onClose }) => {
  return (
    <div>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={styles.tutorialPane}>
        <button className={styles.closeButton} onClick={onClose}>
          <img src={`${process.env.IMAGE_CDN_URL}/images/seek/close.svg`} />
        </button>
        <p>{translations.translation["seek.help.heading"]}</p>
        <div>
          {translations.translation["seek.help.heading2"]}
          <ul>
            {(translations.translation["seek.help.points"] || []).map((point, index) => {
              return <li key={index}>{point}</li>
            })}
          </ul>
          <div className={styles.line}>&nbsp;</div>
          <em>{translations.translation["seek.help.disclaimer"]}</em>
        </div>
      </div>
    </div>
  );
}